import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

export const Navigation = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#services', label: 'Services' },
    { href: '#work', label: 'Our Work' },
    { href: '#voices', label: 'Voices' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-[#0D1B2A]/10 shadow-sm'
            : 'bg-white border-b border-[#0D1B2A]/10'
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#C9A84C]/30 to-transparent blur-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                <img
                  src="/assets/tsalach-logo.png"
                  alt="Tsalach Cleaning Services"
                  className="relative h-12 lg:h-14 w-auto object-contain"
                />
              </div>
              <div className="hidden sm:block leading-none border-l border-[#0D1B2A]/15 pl-3">
                <div className="font-serif text-[#0D1B2A] text-lg lg:text-xl leading-tight">Tsalach</div>
                <div className="text-[#C9A84C] text-[9px] lg:text-[10px] tracking-[0.3em] uppercase font-semibold mt-1">Cleaning Services</div>
              </div>
            </a>

            <div className="hidden lg:flex items-center gap-10">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="relative text-[13px] text-[#0D1B2A]/75 hover:text-[#0D1B2A] tracking-[0.18em] uppercase font-medium transition-colors group"
                  data-testid={`nav-link-${l.label.toLowerCase().replace(' ', '-')}`}
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#C9A84C] transition-all duration-500 group-hover:w-full" />
                </a>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-6">
              <a
                href="tel:5712656239"
                className="flex items-center gap-2 text-[#8F7430] text-sm tracking-wider hover:text-[#C9A84C] transition-colors"
                data-testid="nav-phone"
              >
                <Phone className="w-4 h-4" />
                (571) 265-6239
              </a>
              <button
                onClick={onBook}
                className="relative overflow-hidden group bg-[#0D1B2A] text-white hover:text-[#0D1B2A] px-6 py-2.5 text-xs tracking-[0.25em] uppercase transition-colors duration-500"
                data-testid="nav-book-btn"
              >
                <span className="absolute inset-0 bg-[#C9A84C] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out" />
                <span className="relative">Book Service</span>
              </button>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="lg:hidden text-[#0D1B2A] p-2"
              data-testid="nav-mobile-menu"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0D1B2A] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <img
                  src="/assets/tsalach-logo.png"
                  alt="Tsalach Cleaning Services"
                  className="h-10 w-auto object-contain"
                />
                <button
                  onClick={() => setOpen(false)}
                  className="text-white p-2"
                  data-testid="mobile-menu-close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 flex flex-col justify-center items-center gap-8 px-6">
                {links.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    className="text-3xl font-serif text-white hover:text-[#C9A84C] transition-colors"
                  >
                    {l.label}
                  </motion.a>
                ))}
                <button
                  onClick={() => {
                    setOpen(false);
                    onBook();
                  }}
                  className="mt-8 bg-[#C9A84C] text-[#0D1B2A] px-10 py-4 text-sm tracking-[0.25em] uppercase font-semibold"
                  data-testid="mobile-menu-book-btn"
                >
                  Book Service
                </button>
                <a href="tel:5712656239" className="text-[#C9A84C] mt-4 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> (571) 265-6239
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
